import { makeObservable, observable } from "mobx";

import { CourseModel } from "@courses-models/course";
import { ProgressionModel } from "@courses-models/progression";
import { ElementModel, Engine, PM2Portal } from "@courses-pm2/api";
import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";
import { autobind, throttleToAnimationFrame } from "@use/decorators";

import { mapPm2Components, RendererProps } from "@apps/viewer/src/ts/utils/mapPm2Components";
import { openInternalLink } from "@apps/viewer/src/ts/utils/openInternalLink";

/**
 * Root data store for the application lifecycle.
 *
 * This store will combine all application state, making it straight forward to
 * propagate this throughout the application.
 */
export class Store {
    /**
     * Description of the course that's supposed to be rendered within the
     * application.
     */
    public readonly course: CourseModel;

    /**
     * Description of the user that's currently signed in to the application (if
     * any).
     */
    public readonly user: AuthenticatedUserModel;

    /**
     * Description of the user's current progression within the course, which is
     * used to present him with a prompt asking if he'd like to continue from
     * his last place in the course when opening it.
     */
    public readonly progression: ProgressionModel;

    /**
     * The rendering engine used to transform PM2 element models into React
     * nodes that we can mount into the DOM.
     */
    public readonly engine: Engine<RendererProps>;

    /**
     * Contains the list of portals that exist within the PM2 backend (used to
     * translate portalId --> link).
     */
    public readonly portals: PM2Portal[];

    /**
     * Specifies if a slider component is currently being dragged by a user, in
     * order to determine if swipe/drag navigation or other functionality should
     * be disabled while the slider is active.
     */
    public isDraggingSlider: boolean;

    /**
     * Specifies the id of the currently rendered block, so the application can
     * react to changes.
     */
    public blockId: string;

    /**
     * Specifies whether the app is currently considered being in a mobile state
     */
    public isMobile: boolean = this.isViewportMobile();

    /**
     * Specifies a reference to the model describing an element, which we would
     * like to see opened in a lightbox.
     */
    public lightboxContent?: ElementModel;

    /**
     * Will be true in case the progressionPrompt should be displayed.
     */
    public displayProgressionPrompt: boolean = false;

    /**
     * Apply initial data when the store is created, so it's instantly available
     * when referencing items on the store.
     */
    constructor(initial: {
        course: CourseModel;
        user: AuthenticatedUserModel;
        progression: ProgressionModel;
        portals: PM2Portal[];
    }) {
        this.course = initial.course;
        this.user = initial.user;
        this.progression = initial.progression;
        this.portals = initial.portals;

        makeObservable(this, {
            blockId: observable,
            isMobile: observable,
            lightboxContent: observable,
            displayProgressionPrompt: observable,
        })

        // Create a custom mapper method, which will propagate certain arguments
        // to the mapPm2Components utility
        const mapper = async (model: new (...args: unknown[]) => ElementModel) => {
            return mapPm2Components(model, {
                // Helper that'll register the element model describing the
                // content that's supposed to be opened in the lightbox
                openLightbox: (args: { model: ElementModel }) => {
                    this.lightboxContent = args.model;
                },

                // Helper that'll open internal links on portals in a new window
                // bringing the user directly to the portal
                openInternalLink: (args: { path: string; portalId: string }) => {
                    openInternalLink(args)
                        .catch(() => { /* Errors are handled internally, ignore silently... */ });
                },

                // Helper to be executed on drag start in order to disable swipe
                // logic
                onSliderDragStart: () => {
                    this.isDraggingSlider = true;
                },

                // Helper to be executed on drag end in order to enable swipe logic
                // again.
                onSliderDragEnd: () => {
                    this.isDraggingSlider = false;
                },
            });
        };

        // Create the engine that's going to power rendering of the data models
        // from pm2 content feeds
        this.engine = new Engine({
            mapper,
            user: this.user,
        });

        window.addEventListener("resize", this.onResize);
        window.addEventListener("orientationchange", this.onResize);
    }

    /**
     * Simple helper that determines whether the viewport is currently considered
     * as being in a mobile state.
     */
    private isViewportMobile(): boolean {
        return window.innerWidth <= 768;
    }

    /**
     * Listener that'll get called every time the viewport resizes in order to
     * determine the current viewport breakpoint
     */
    @autobind()
    @throttleToAnimationFrame()
    private onResize(): void {
        this.isMobile = this.isViewportMobile();
    }
}
